import React, { useState, useRef, useEffect } from "react";
import Tooltip from "@material-ui/core/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { toast } from "react-toastify";
// import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import AppBar from "@mui/material/AppBar";
import Autocomplete from "@mui/material/Autocomplete";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar, Table } from "../components";
import { deleteCar, getCars, getDealers } from "../../../redux/actions/apiActions";
import setGlobalLoader from "../../../redux/actions/loaderActions";
import { PROFILES } from "../../../utils/constants";
import exportToExcel from "../../../utils/exportToExcel";

const TableComponent = React.forwardRef(({ columns, data, title, isRemoteData }, ref) => {
  const theme = useTheme();

  return (
    <Box p={4}>
      <Box
        width={1}
        height={1}
        // minHeight={800}
        borderRadius={2}
        border={`2px solid ${theme.palette.divider}`}
        sx={{
          borderStyle: "dashed",
        }}
      >
        <Table
          ref={ref}
          columns={columns}
          data={data}
          export={false}
          // search
          selection={false}
          toolbar
          title={title}
          isRemoteData={isRemoteData}
        />
      </Box>
    </Box>
  );
});

const Cars = () => {
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cars, setCars] = useState([]);
  const theme = useTheme();
  const profile = useSelector((state) => state.authReducer.profile);
  const tableRef = useRef(null);
  const isMd = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });

  const [openSidebar, setOpenSidebar] = useState(false);

  // Date filter state - Admin: 1 month default, Dealer: no default dates
  const today = new Date();
  const minDateObj = new Date("2025-01-01");

  // Calculate default dates based on profile
  const getDefaultStartDate = () => {
    if (profile === PROFILES.ADMIN) {
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }
    return null; // No default for Dealer
  };

  const getDefaultEndDate = () => {
    if (profile === PROFILES.ADMIN) {
      return today;
    }
    return null; // No default for Dealer
  };

  const [dateRange, setDateRange] = useState([
    {
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate(),
      key: "selection",
    },
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Getter functions for startDate and endDate
  const startDate = dateRange[0].startDate;
  const endDate = dateRange[0].endDate;

  // Handle date range change
  const handleDateRangeChange = (item) => {
    setDateRange([item.selection]);
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState("");

  // Fetch dealers for admin dropdown
  useEffect(() => {
    const fetchDealers = async () => {
      if (profile === PROFILES.ADMIN) {
        try {
          const dealerRes = await getDealers("?limit=10000");
          setDealers(dealerRes?.data?.data?.docs || []);
        } catch (error) {
          console.log("Error fetching dealers", error);
        }
      }
    };
    fetchDealers();
  }, [profile]);

  const handleSidebarOpen = () => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = () => {
    setOpenSidebar(false);
  };
  const open = isMd ? false : openSidebar;

  const handleDeleteCar = async (rowData) => {
    const text = `Are you sure you want to delete ${rowData.make} ${rowData.model} ${rowData.trim} ${rowData.year}`;
    if (window.confirm(text) === true) {
      dispatch(setGlobalLoader(true));
      try {
        await deleteCar(rowData._id);
        setCars(cars.filter((d) => d._id !== rowData._id));
        dispatch(setGlobalLoader(false));
      } catch (error) {
        dispatch(setGlobalLoader(false));
        console.log("delete car error", error);
        toast.error(error.response.data.msg);
      }
    } else {
      toast.success("Request Cancelled");
    }
  };

  const adminHeaders = [
    "Sr no.",
    "Title",
    "Dealer",
    "Price",
    "Registered On",
  ];
  const adminColumns = [
    { title: "Sr no.", field: "index", filtering: false,},
    {
      title: "Image",
      filtering: false,
      render: (rowData) =>
        rowData.heroImageUrl ? (
          <img
            src={rowData.heroImageUrl}
            alt="Car"
            style={{ width: 160, height: 100, objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          "-"
        ),
    },
    { title: "Title", render: (rowData) => rowData.title, filtering: false },
    { title: "Dealer", field: "dealerId.name", filtering: false },
    { title: "Price", field: "price", filtering: false },
    { title: "Registered On", field: "registeredOn", filtering: false },
    {
      title: "Actions",
      field: "",
      filtering: false,
      render: (rowData) => (
        <div className="tableActions ">
          <Tooltip title="Delete">
            <DeleteIcon onClick={() => handleDeleteCar(rowData)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Fetch data with server-side pagination
  const fetchCars = async (query) => {
    return new Promise(async (resolve) => {
      try {
        dispatch(setGlobalLoader(true));

        // Build query string for API
        const page = query.page + 1; // MaterialTable uses 0-based index, API uses 1-based
        const limit = query.pageSize;
        const sort = query.orderBy ? { [query.orderBy.field]: query.orderDirection } : {};

        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        // Add sort if exists
        if (Object.keys(sort).length > 0) {
          queryParams.append('sort', JSON.stringify(sort));
        }

        // Add date filters
        if (startDate) {
          queryParams.append('startDate', format(startDate, "yyyy-MM-dd"));
        }
        if (endDate) {
          queryParams.append('endDate', format(endDate, "yyyy-MM-dd"));
        }

        // Add search filter
        if (searchTerm) {
          queryParams.append('searchTerm', searchTerm);
        }

        // Add dealer filter (admin only)
        if (selectedDealer) {
          queryParams.append('dealerId', selectedDealer);
        }

        const carRes = await getCars(`?${queryParams.toString()}`);
        const apiData = carRes?.data?.data;

        const data = apiData?.docs?.map((d, i) => ({
          ...d,
          index: (page - 1) * limit + i + 1,
          registeredOn: format(new Date(d?.createdAt), "MMM dd, yyyy"),
          images: d?.images?.map((image) => image.url),
        }));

        dispatch(setGlobalLoader(false));

        resolve({
          data: data,
          page: query.page,
          totalCount: apiData?.total || 0,
        });
      } catch (error) {
        dispatch(setGlobalLoader(false));
        console.log("get cars error", error);
        toast.error(error?.response?.data?.msg || "Error fetching cars");
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
      }
    });
  };

  return (
    <Box>
      <AppBar
        position={"fixed"}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? "permanent" : "temporary"}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <Box
          display="flex"
          flex="1 1 auto"
          overflow="hidden"
          paddingLeft={{ md: "256px" }}
        >
          <Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto">
              <Box
                marginTop={3}
                mx={{ xs: 3, md: 4 }}
                display={"flex"}
                flexDirection={"column"}
                gap={2}
              >
                {/* Row 1: Add Car and Download buttons */}
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    component="button"
                    disabled
                    size="large"
                    onClick={() => {
                      toast.info("coming soon");
                    }}
                  >
                    Add Car
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    component="button"
                    size="medium"
                    onClick={async () => {
                      try {
                        dispatch(setGlobalLoader(true));
                        // Fetch all cars for export with date filters
                        const exportParams = new URLSearchParams({ limit: "10000" });
                        if (startDate) exportParams.append('startDate', format(startDate, "yyyy-MM-dd"));
                        if (endDate) exportParams.append('endDate', format(endDate, "yyyy-MM-dd"));
                        if (selectedDealer) exportParams.append('dealerId', selectedDealer);
                        const carRes = await getCars(`?${exportParams.toString()}`);
                      const allCars = carRes?.data?.data?.docs?.map((d, i) => ({
                        ...d,
                        index: i + 1,
                        registeredOn: format(new Date(d?.createdAt), "MMM dd, yyyy"),
                      }));

                      const rows = allCars.map((row) => ({
                        "Sr No. ": row.index,
                        title: row.title,
                        dealer: row.dealerId.name,
                        price: row.price,
                        registeredOn: row.registeredOn,
                      }));
                      await exportToExcel(rows, adminHeaders, "Cars");
                      dispatch(setGlobalLoader(false));
                    } catch (error) {
                      dispatch(setGlobalLoader(false));
                      console.log("export error", error);
                      toast.error("Error exporting cars");
                    }
                  }}
                >
                  Download
                </Button>
                </Box>

                {/* Row 2: Date Filters (left) and Search (right) */}
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  {/* Date Filters - Left */}
                  <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        size="small"
                        value={
                          startDate && endDate
                            ? `${format(startDate, "MMM dd, yyyy")} - ${format(endDate, "MMM dd, yyyy")}`
                            : ""
                        }
                        placeholder="Select date range"
                        onClick={() => setShowDatePicker(!showDatePicker)}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthIcon />
                            </InputAdornment>
                          ),
                          endAdornment: startDate && endDate && (
                            <InputAdornment position="end">
                              <ClearIcon
                                sx={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDateRange([
                                    {
                                      startDate: null,
                                      endDate: null,
                                      key: "selection",
                                    },
                                  ]);
                                  setShowDatePicker(false);
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 280, cursor: "pointer" }}
                      />
                      {showDatePicker && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            zIndex: 1000,
                            mt: 1,
                            boxShadow: 3,
                            borderRadius: 1,
                            bgcolor: "background.paper",
                          }}
                        >
                          <DateRangePicker
                            onChange={handleDateRangeChange}
                            ranges={[
                              {
                                startDate: dateRange[0].startDate || new Date(),
                                endDate: dateRange[0].endDate || new Date(),
                                key: "selection",
                              },
                            ]}
                            minDate={minDateObj}
                            maxDate={today}
                            rangeColors={[theme.palette.primary.main]}
                            locale={enUS}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              p: 1,
                              borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Button
                              size="small"
                              onClick={() => setShowDatePicker(false)}
                            >
                              Close
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    {profile === PROFILES.ADMIN && (
                      <Autocomplete
                        size="small"
                        options={dealers}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option?.name || "";
                        }}
                        value={dealers.find(dealer => dealer._id === selectedDealer) || null}
                        onChange={(_, newValue) => setSelectedDealer(newValue?._id || "")}
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Dealer"
                            placeholder="Search dealer..."
                          />
                        )}
                        sx={{ minWidth: 250 }}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                      />
                    )}
                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      onClick={() => {
                        if (tableRef.current) {
                          tableRef.current.onQueryChange();
                        }
                      }}
                    >
                      Filter
                    </Button>
                    <Button
                      variant="text"
                      color="error"
                      size="medium"
                      onClick={() => {
                        setDateRange([
                          {
                            startDate: getDefaultStartDate(),
                            endDate: getDefaultEndDate(),
                            key: "selection",
                          },
                        ]);
                        setSearchTerm("");
                        setSelectedDealer("");
                        setShowDatePicker(false);
                        setTimeout(() => {
                          if (tableRef.current) {
                            tableRef.current.onQueryChange();
                          }
                        }, 100);
                      }}
                    >
                      Reset
                    </Button>
                  </Box>

                  {/* Search - Right */}
                  <Box display="flex" gap={2} alignItems="center">
                    <TextField
                      label="Search by Title"
                      type="text"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchTerm.length >= 4 && tableRef.current) {
                          tableRef.current.onQueryChange();
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <ClearIcon
                              sx={{ cursor: 'pointer' }}
                              onClick={() => {
                                setSearchTerm("");
                                setTimeout(() => {
                                  if (tableRef.current) {
                                    tableRef.current.onQueryChange();
                                  }
                                }, 100);
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 250 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      disabled={searchTerm.length < 4}
                      onClick={() => {
                        if (tableRef.current) {
                          tableRef.current.onQueryChange();
                        }
                      }}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              </Box>
              <TableComponent
                ref={tableRef}
                data={fetchCars}
                columns={adminColumns}
                title="Cars"
                isRemoteData={true}
              />
              <Divider />
            </Box>
          </Box>
        </Box>
      </main>
    </Box>
  );
};

export default Cars;
